/**
 * Financial field gating.
 *
 * Source of truth: docs/phase0/04-permissions-matrix.md §"Financial field gating"
 * Decision: ADR-005 — fields are OMITTED from responses, never nulled or zeroed.
 *
 * Returning `null` is not acceptable: it is indistinguishable from "no value",
 * and it tempts client-side-only hiding, which is one dev-tools tab away from a
 * leak. The data must not cross the wire.
 */

import { isExternalRole, type Role } from './types.js';

/**
 * Entity field paths gated by `finance:view`, for entities that are only
 * PARTLY financial. Wholly-financial resources (budget, cost, variation,
 * billing) are gated at the resource level — see `FINANCIAL_RESOURCES`.
 */
export const GATED_FIELDS: Readonly<Record<string, readonly string[]>> = Object.freeze({
  project: Object.freeze(['contract_value', 'revised_contract_value']),
  material_item: Object.freeze(['unit_cost', 'total_cost', 'po_value']),
  variation: Object.freeze(['cost_impact', 'value_impact', 'margin_impact', 'quoted_amount']),
  rfq: Object.freeze(['quoted_amount']),
  purchase_order_ref: Object.freeze(['po_value']),
  work_package: Object.freeze(['value']),
  forecast: Object.freeze(['*']),
  budget_line: Object.freeze(['*']),
  commitment: Object.freeze(['*']),
  cost_entry: Object.freeze(['*']),
  invoice_summary: Object.freeze(['*']),
  receipt_summary: Object.freeze(['*']),
});

/**
 * The Pulse commercial signal is omitted wholesale for users without
 * finance:view, and `overall` is then computed from the other five signals.
 * See `06-dependency-and-pulse-rules.md` §"Signal 6".
 */
export const GATED_PULSE_SIGNAL = 'commercial' as const;

/**
 * Can this role EVER hold finance:view?
 *
 * External roles cannot, at the role level rather than the membership level,
 * so it cannot be granted to them by a misconfigured membership. This is the
 * control that stops a permissions mistake becoming a commercial incident —
 * a client must never see the contractor's margin.
 */
export function canHoldFinanceView(role: Role): boolean {
  return !isExternalRole(role);
}

/** Default finance:view for a new project membership, by role. */
export function defaultFinanceView(role: Role): boolean {
  switch (role) {
    case 'company_admin':
    case 'director':
    case 'qs_commercial':
      return true;
    case 'project_manager':
      // Granted by default but explicitly revocable per project.
      return true;
    default:
      return false;
  }
}

/**
 * Strip gated fields from an entity payload.
 *
 * Deletes the keys outright. Callers MUST apply this at the serialisation
 * boundary, not in the UI.
 */
export function stripFinancialFields<T extends Record<string, unknown>>(
  entityName: string,
  payload: T,
  hasFinanceView: boolean,
): Partial<T> {
  if (hasFinanceView) return payload;

  const gated = GATED_FIELDS[entityName];
  if (!gated) return payload;

  if (gated.includes('*')) return {} as Partial<T>;

  const out: Partial<T> = { ...payload };
  for (const field of gated) {
    delete out[field as keyof T];
  }
  return out;
}
